import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Container,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  ListItem,
  SimpleGrid,
  Spinner,
  Text,
  UnorderedList,
  VStack,
} from "@chakra-ui/react";
import PageHeader from "../components/PageHeader";
import PoolCardScroller from "../components/PoolCardScroller";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import {
  getMySurvivorPools,
  getAllSurvivorPools,
  createSurvivorPool,
  joinSurvivorPool,
} from "../api/nflSurvivor";
import { useNflSeason } from "../hooks/useNflSeason";
import nflLogo from "../assets/logos/nfl.png";

const TOP_BAR_BLUE = "#00005f";
const PAGE_BLUE = "#00007f";

export default function NflSurvivorHome() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { season, seasonId, loading: seasonLoading } = useNflSeason();
  // Three lobby states: too early, open for setup, season under way.
  const creationOpen = !!season?.pool_creation_open;
  const seasonStarted = !!season?.season_started;

  const [myPools, setMyPools] = useState([]);
  const [allPools, setAllPools] = useState([]);
  const [loadingPools, setLoadingPools] = useState(true);
  const [poolError, setPoolError] = useState("");

  const [newPoolName, setNewPoolName] = useState("");
  const [newPoolDepth, setNewPoolDepth] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [joiningPoolId, setJoiningPoolId] = useState(null);

  useEffect(() => {
    if (!seasonId) {
      if (!seasonLoading) setLoadingPools(false);
      return;
    }
    loadPools();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasonId, seasonLoading]);

  async function loadPools() {
    setLoadingPools(true);
    setPoolError("");
    try {
      const [mine, all] = await Promise.all([
        getMySurvivorPools(seasonId),
        getAllSurvivorPools(seasonId),
      ]);
      setMyPools(mine);
      setAllPools(all);
    } catch (err) {
      setPoolError(err?.response?.data?.detail || "Failed to load pools.");
      setMyPools([]);
      setAllPools([]);
    } finally {
      setLoadingPools(false);
    }
  }

  const joinablePools = useMemo(() => {
    const mine = new Set(myPools.map((p) => p.id));
    return allPools.filter((p) => !mine.has(p.id) && !p.is_private);
  }, [myPools, allPools]);

  function handleOpenPool(poolId) {
    navigate(`/nfl-survivor/current/${poolId}`, { state: { seasonId } });
  }

  async function handleJoinPool(poolId) {
    setJoiningPoolId(poolId);
    setPoolError("");
    try {
      await joinSurvivorPool(seasonId, poolId);
      await loadPools();
      navigate(`/nfl-survivor/current/${poolId}`, { state: { seasonId } });
    } catch (err) {
      setPoolError(err?.response?.data?.detail || "Failed to join pool.");
    } finally {
      setJoiningPoolId(null);
    }
  }

  async function handleCreatePool() {
    const name = newPoolName.trim();
    if (!name) {
      setCreateError("Pool name is required.");
      return;
    }
    const depth = newPoolDepth ? Number(newPoolDepth) : null;
    if (depth !== null && (!Number.isInteger(depth) || depth < 1)) {
      setCreateError("Pool depth must be a whole number greater than 0.");
      return;
    }

    setCreating(true);
    setCreateError("");
    try {
      const pool = await createSurvivorPool(seasonId, { name, pool_depth: depth });
      setNewPoolName("");
      setNewPoolDepth("");
      navigate(`/nfl-survivor/setup/${pool.id}`, { state: { seasonId } });
    } catch (err) {
      setCreateError(err?.response?.data?.detail || "Failed to create pool.");
    } finally {
      setCreating(false);
    }
  }

  return (
    <Box minH="100vh" bg={PAGE_BLUE} color="white" overflowX="hidden">
      <PageHeader
        title="NFL SURVIVOR LOBBY"
        onBack={() => navigate("/")}
        user={user}
        onLogout={() => { logout(); navigate("/login", { replace: true }); }}
      />

      <Container maxW="container.xl" py={10}>
        <VStack align="stretch" spacing={8}>
          <VStack align="start" spacing={2}>
            <Text fontSize="lg" color="whiteAlpha.900">
              Welcome to the NFL Survivor lobby. Enter your current game, view open pools, or start a new pool.
            </Text>
            {creationOpen && season && (
              <Alert status="success" borderRadius="md" mt={1} color="green.900">
                <AlertIcon flexShrink={0} />
                <Text ml={2}>
                  The <strong>{season.name}</strong> season is available for set up — create your pool below!
                </Text>
              </Alert>
            )}
          </VStack>

          {poolError && (
            <Alert status="error" borderRadius="md"><AlertIcon />{poolError}</Alert>
          )}

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            {/* Your Active Games */}
            <Box
              bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`}
              borderRadius="lg" boxShadow="lg" p={8} minH="260px" minW="0"
              position="relative" overflow="hidden"
              opacity={!loadingPools && myPools.length === 0 ? 0.5 : 1}
            >
              <Box position="absolute" inset={0} display="flex" alignItems="center"
                   justifyContent="center" opacity={0.06} pointerEvents="none" zIndex={0}>
                <img src={nflLogo} alt="" style={{ width: "260px" }} />
              </Box>

              <VStack align="stretch" spacing={4} h="100%" position="relative" zIndex={1}>
                <Heading size="md" color={TOP_BAR_BLUE}>Your Active Games</Heading>
                <Text color="gray.700">Survivor pools you belong to this season.</Text>

                {loadingPools ? (
                  <Flex flex="1" align="center" justify="center"><Spinner /></Flex>
                ) : myPools.length === 0 ? (
                  <Flex flex="1" align="center" justify="center">
                    <Text fontSize="lg" color="gray.500" textAlign="center">
                      You are not currently in any pools.
                    </Text>
                  </Flex>
                ) : (
                  <Box maxH={{ base: "240px", sm: "unset" }} overflowY={{ base: "auto", sm: "visible" }} mt={1}>
                    {/* Mobile: compact list */}
                    <VStack align="stretch" spacing={0} display={{ base: "flex", sm: "none" }}>
                      {myPools.map((pool) => (
                        <Flex key={pool.id} align="center" py={2} borderBottom="1px solid" borderColor="gray.100">
                          <Text fontWeight="semibold" fontSize="sm" flex="1" mr={3} noOfLines={1}>{pool.name}</Text>
                          <HStack spacing={3} flexShrink={0}>
                            {pool.owner_user_id === user?.id && (
                              <Button variant="link" size="sm" color={TOP_BAR_BLUE}
                                      onClick={() => navigate(`/nfl-survivor/setup/${pool.id}`, { state: { seasonId } })}>Edit</Button>
                            )}
                            <Button variant="link" size="sm" color={TOP_BAR_BLUE} fontWeight="bold"
                                    onClick={() => handleOpenPool(pool.id)}>Open</Button>
                          </HStack>
                        </Flex>
                      ))}
                    </VStack>
                    {/* Desktop: horizontally scrolling cards */}
                    <PoolCardScroller display={{ base: "none", sm: "block" }}>
                      {myPools.map((pool) => (
                        <Box key={pool.id} border="1px solid" borderColor="gray.200" borderRadius="md" p={4} bg="gray.50">
                          <VStack align="center" spacing={2}>
                            <Text fontWeight="bold" textAlign="center">{pool.name}</Text>
                            <Text fontSize="sm" color="gray.600">Entries: {pool.entry_count}</Text>
                            <Text fontSize="sm" color="gray.600">
                              Alive: <strong>{pool.alive_count}</strong> / {pool.entry_count}
                            </Text>
                            {pool.total_pot != null && (
                              <Text fontSize="sm" color="gray.600">Total Pot: ${pool.total_pot}</Text>
                            )}
                            <HStack spacing={2} justify="center">
                              {pool.owner_user_id === user?.id && (
                                <Button size="sm" variant="outline" borderColor={TOP_BAR_BLUE} color={TOP_BAR_BLUE}
                                        _hover={{ bg: "gray.100" }}
                                        onClick={() => navigate(`/nfl-survivor/setup/${pool.id}`, { state: { seasonId } })}>
                                  Edit Pool
                                </Button>
                              )}
                              <Button size="sm" bg={TOP_BAR_BLUE} color="white" _hover={{ bg: "#00004a" }}
                                      onClick={() => handleOpenPool(pool.id)}>Open Pool</Button>
                            </HStack>
                          </VStack>
                        </Box>
                      ))}
                    </PoolCardScroller>
                  </Box>
                )}
              </VStack>
            </Box>

            {/* Join an Open Pool */}
            <Box
              bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`}
              borderRadius="lg" boxShadow="lg" p={8} minH="260px" minW="0"
              position="relative" overflow="hidden"
              opacity={!loadingPools && joinablePools.length === 0 ? 0.5 : 1}
            >
              <Box position="absolute" inset={0} display="flex" alignItems="center"
                   justifyContent="center" opacity={0.06} pointerEvents="none" zIndex={0}>
                <img src={nflLogo} alt="" style={{ width: "260px" }} />
              </Box>

              <VStack align="stretch" spacing={5} h="100%" position="relative" zIndex={1}>
                <Heading size="md" color={TOP_BAR_BLUE}>Join an Open Pool</Heading>
                <Text color="gray.700">Available pools for the current season.</Text>

                {loadingPools ? (
                  <Flex flex="1" align="center" justify="center"><Spinner /></Flex>
                ) : joinablePools.length === 0 ? (
                  <Flex flex="1" align="center" justify="center">
                    <Text fontSize="lg" color="gray.500" textAlign="center">
                      No open pools available right now.
                    </Text>
                  </Flex>
                ) : (
                  <PoolCardScroller>
                    {joinablePools.map((pool) => {
                      // is_open is false at both ends of the window, so use
                      // opens_at to tell "too early" apart from "kicked off".
                      const opensAt = pool.opens_at ? new Date(pool.opens_at) : null;
                      const notYetOpen = !!opensAt && opensAt > new Date();
                      return (
                      <Box key={pool.id} border="1px solid" borderColor="gray.200" borderRadius="md" p={4} bg="gray.50">
                        <VStack align="center" spacing={2}>
                          <Text fontWeight="bold" textAlign="center">{pool.name}</Text>
                          <Text fontSize="sm" color="gray.600">Pool Depth: {pool.pool_depth ?? "—"}</Text>
                          <Text fontSize="sm" color="gray.600">Entries: {pool.entry_count}</Text>
                          {pool.total_pot != null && (
                            <Text fontSize="sm" color="gray.600">Total Pot: ${pool.total_pot}</Text>
                          )}
                          {pool.is_open === false ? (
                            <Button size="sm" isDisabled>
                              {notYetOpen
                                ? `Opens ${opensAt.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
                                : "Entries closed"}
                            </Button>
                          ) : (
                            <Button size="sm" bg={TOP_BAR_BLUE} color="white" _hover={{ bg: "#00004a" }}
                                    isLoading={joiningPoolId === pool.id}
                                    onClick={() => handleJoinPool(pool.id)}>
                              Join Pool
                            </Button>
                          )}
                        </VStack>
                      </Box>
                      );
                    })}
                  </PoolCardScroller>
                )}
              </VStack>
            </Box>
          </SimpleGrid>

          {/* Rules + create */}
          <Box bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg" boxShadow="lg" p={8}>
            <VStack align="stretch" spacing={8}>
              {/* 1 — Want to Start a New Pool? */}
              <Box>
                <Heading size="md" mb={3} color={TOP_BAR_BLUE}>Want to Start a New Pool?</Heading>
                {seasonStarted && (
                  <Alert status="warning" borderRadius="md">
                    <AlertIcon />
                    New games can&apos;t be created mid season
                  </Alert>
                )}
              </Box>

              {/* 2 — Rules */}
              <Box>
                <Heading size="md" mb={3} color={TOP_BAR_BLUE}>Rules</Heading>
                <UnorderedList spacing={3} pl={5}>
                  <ListItem>Pick <strong>one winner</strong> each week.</ListItem>
                  <ListItem>A team can only be picked <strong>once during the season</strong>.</ListItem>
                  <ListItem>If your team wins, you stay in.</ListItem>
                  <ListItem>If your team <strong>loses or ties</strong>, you are out.</ListItem>
                  <ListItem>Picks lock at that game&apos;s kickoff — you can still pick a later game in the same week.</ListItem>
                  <ListItem>Miss a week and an autopick is applied for you: the team with the <strong>worst odds</strong> still available.</ListItem>
                  <ListItem>Teams on a bye can&apos;t be picked that week.</ListItem>
                </UnorderedList>
              </Box>

              <Divider borderColor="gray.300" />

              {/* 3 — Pool Name / Depth / Button */}
              <Box opacity={creationOpen ? 1 : 0.5}>
                {createError && (
                  <Alert status="error" borderRadius="md" mb={4}><AlertIcon />{createError}</Alert>
                )}
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                  <FormControl isRequired isDisabled={!creationOpen}>
                    <FormLabel>Pool Name</FormLabel>
                    <Input value={newPoolName} placeholder="Enter pool name"
                           onChange={(e) => { setNewPoolName(e.target.value); setCreateError(""); }} />
                  </FormControl>
                  <FormControl isDisabled={!creationOpen}>
                    <FormLabel>Pool Depth ($)</FormLabel>
                    <Input type="number" min={1} value={newPoolDepth} placeholder="Optional buy-in amount"
                           onChange={(e) => setNewPoolDepth(e.target.value)} />
                  </FormControl>
                </SimpleGrid>
                <Button bg={TOP_BAR_BLUE} color="white" _hover={{ bg: "#00004a" }}
                        isLoading={creating} loadingText="Creating..."
                        isDisabled={!seasonId || !creationOpen}
                        onClick={handleCreatePool}>
                  Start New Pool
                </Button>
                {!creationOpen && !seasonStarted && (
                  <Text mt={4} fontSize="sm" color="gray.600">
                    Once next season setup is available, this page will allow users to create a
                    new pool before the season begins.
                  </Text>
                )}
                {creationOpen && (
                  <Text mt={4} fontSize="sm" color="gray.600">
                    You&apos;ll set buy-back rules, the all-eliminated rule and payouts on the next screen.
                  </Text>
                )}
              </Box>
            </VStack>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
}
